'use client'

import React from 'react'
import {
  Search,
  Bot,
  MessageSquare,
  Layers,
  Target,
  BarChart3,
  Globe,
  ArrowRight,
  CheckCircle2,
  XCircle,
  BookOpen,
  Zap,
  Award,
  Quote,
  ListOrdered,
  FileText,
} from 'lucide-react'

/**
 * SEO vs GEO vs AEO Diagrams
 * Visual components for Part 2 of The AI Search Playbook
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * Three Disciplines Comparison
 */
export function ThreeDisciplinesDiagram() {
  const disciplines = [
    {
      name: 'SEO',
      fullName: 'Search Engine Optimization',
      target: 'Google, Bing (10 blue links)',
      goal: 'Rank in search results',
      metric: 'Rankings, organic traffic, CTR',
      born: '~1997',
      color: '#6b7280',
      icon: Search,
      status: 'Evolving',
    },
    {
      name: 'AEO',
      fullName: 'Answer Engine Optimization',
      target: 'Featured snippets, voice assistants, knowledge panels',
      goal: 'Become the cited answer',
      metric: 'Featured snippet wins, voice search appearances',
      born: '2018 (Jason Barnard, Kalicube)',
      color: '#FDC304',
      icon: MessageSquare,
      status: 'Merging into GEO',
    },
    {
      name: 'GEO',
      fullName: 'Generative Engine Optimization',
      target: 'ChatGPT, Perplexity, Claude, Gemini, AI Overviews',
      goal: 'Become the recommended brand',
      metric: 'Citations, brand mentions, share of voice',
      born: '2024 (Princeton, Georgia Tech, IIT Delhi)',
      color: '#008dcc',
      icon: Bot,
      status: 'The new frontier',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Layers className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            The Three Disciplines of Search Optimization
          </span>
        </div>
        <div className='space-y-4'>
          {disciplines.map(d => {
            const Icon = d.icon
            return (
              <div
                key={d.name}
                className='rounded-lg border-2 p-4'
                style={{ borderColor: `${d.color}40` }}
              >
                <div className='mb-3 flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Icon className='h-5 w-5' style={{ color: d.color }} />
                    <span
                      className='text-lg font-black'
                      style={{ color: d.color }}
                    >
                      {d.name}
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      {d.fullName}
                    </span>
                  </div>
                  <span
                    className='rounded-full px-2 py-0.5 text-xs font-bold text-white'
                    style={{ backgroundColor: d.color }}
                  >
                    {d.status}
                  </span>
                </div>
                <div className='grid grid-cols-1 gap-2 text-xs sm:grid-cols-2'>
                  <div>
                    <span className='font-semibold text-foreground/80'>
                      Target:{' '}
                    </span>
                    <span className='text-muted-foreground'>{d.target}</span>
                  </div>
                  <div>
                    <span className='font-semibold text-foreground/80'>
                      Goal:{' '}
                    </span>
                    <span className='text-muted-foreground'>{d.goal}</span>
                  </div>
                  <div>
                    <span className='font-semibold text-foreground/80'>
                      Metrics:{' '}
                    </span>
                    <span className='text-muted-foreground'>{d.metric}</span>
                  </div>
                  <div>
                    <span className='font-semibold text-foreground/80'>
                      Origin:{' '}
                    </span>
                    <span className='text-muted-foreground'>{d.born}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Generative Engine Types
 */
export function GenerativeEngineTypesDiagram() {
  const types = [
    {
      name: 'LLM-Native',
      description:
        'Pure language models with trained knowledge. No real-time search.',
      examples: 'ChatGPT or Claude answering without search',
      howCited:
        'Must exist in training data. Brand authority and content quality matter most.',
      color: '#8b5cf6',
      icon: Bot,
    },
    {
      name: 'Search-Augmented',
      description:
        'AI models that search the web before answering. Real-time retrieval.',
      examples:
        'Perplexity, Google AI Overviews, ChatGPT or Claude with search',
      howCited:
        'Content must be crawlable, well-structured, and topically authoritative.',
      color: '#008dcc',
      icon: Globe,
    },
    {
      name: 'Hybrid',
      description: 'Combines trained knowledge with optional real-time search.',
      examples: 'Gemini, Copilot, Meta AI',
      howCited:
        'Both training data presence and live content quality affect citations.',
      color: '#FDC304',
      icon: Layers,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Bot className='h-5 w-5 text-[#8b5cf6]' />
          <span className='text-lg font-semibold text-foreground'>
            Three Types of Generative Engines
          </span>
        </div>
        <div className='space-y-4'>
          {types.map(type => {
            const Icon = type.icon
            return (
              <div
                key={type.name}
                className='rounded-lg border p-4'
                style={{ borderColor: `${type.color}40` }}
              >
                <div className='mb-2 flex items-center gap-2'>
                  <div
                    className='flex h-8 w-8 items-center justify-center rounded-lg'
                    style={{ backgroundColor: `${type.color}15` }}
                  >
                    <Icon className='h-4 w-4' style={{ color: type.color }} />
                  </div>
                  <span className='text-sm font-bold text-foreground'>
                    {type.name}
                  </span>
                </div>
                <p className='mb-2 text-xs text-muted-foreground'>
                  {type.description}
                </p>
                <div className='mb-2 rounded bg-muted p-2'>
                  <span className='text-xs font-semibold text-foreground/80'>
                    Examples:{' '}
                  </span>
                  <span className='text-xs text-muted-foreground'>
                    {type.examples}
                  </span>
                </div>
                <div className='flex items-start gap-1.5'>
                  <ArrowRight
                    className='mt-0.5 h-3 w-3 shrink-0'
                    style={{ color: type.color }}
                  />
                  <span
                    className='text-xs font-medium'
                    style={{ color: type.color }}
                  >
                    {type.howCited}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Source: Aggarwal et al. &ldquo;Let the LLMs Talk&rdquo; (2024),
          adapted
        </p>
      </div>
    </div>
  )
}

/**
 * GEO Ranking Factors
 */
export function GEORankingFactorsDiagram() {
  const factors = [
    {
      name: 'Brand Search Volume',
      correlation: 0.334,
      label: '0.334',
      description: 'How many people search for your brand by name',
      icon: Award,
      color: '#008dcc',
    },
    {
      name: 'Content with Statistics',
      correlation: 0.3,
      label: '+30%',
      description: 'Articles that include specific data points and numbers',
      icon: BarChart3,
      color: '#FDC304',
    },
    {
      name: 'Direct Quotations',
      correlation: 0.28,
      label: '+28%',
      description: 'Including expert quotes and attributed statements',
      icon: Quote,
      color: '#8b5cf6',
    },
    {
      name: 'Structured Content (Listicles)',
      correlation: 0.25,
      label: '+25%',
      description: 'Numbered lists, clear hierarchy, scannable format',
      icon: ListOrdered,
      color: '#10b981',
    },
    {
      name: 'Technical Depth',
      correlation: 0.22,
      label: '+22%',
      description: 'Detailed, authoritative treatment of the subject',
      icon: FileText,
      color: '#F90B8A',
    },
  ]

  const maxCorrelation = 0.35

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Target className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            What Makes AI Cite Your Content
          </span>
        </div>
        <div className='space-y-3'>
          {factors.map(factor => {
            const Icon = factor.icon
            return (
              <div key={factor.name} className='flex items-center gap-3'>
                <div className='flex w-36 shrink-0 items-center gap-1.5'>
                  <Icon
                    className='h-3.5 w-3.5 shrink-0'
                    style={{ color: factor.color }}
                  />
                  <span className='text-xs font-medium text-foreground/80'>
                    {factor.name}
                  </span>
                </div>
                <div className='flex-1'>
                  <div className='h-7 w-full rounded-lg bg-muted'>
                    <div
                      className='flex h-7 items-center justify-end rounded-lg px-2 transition-all duration-500'
                      style={{
                        width: `${(factor.correlation / maxCorrelation) * 100}%`,
                        backgroundColor: factor.color,
                        minWidth: '40px',
                      }}
                    >
                      <span className='text-xs font-bold text-white'>
                        {factor.label}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className='mt-4 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20 p-3'>
          <p className='text-xs text-muted-foreground'>
            <strong>Key insight:</strong> Brand search volume has the strongest
            correlation with AI citations. Being known matters more than any
            on-page tactic.
          </p>
        </div>
        <p className='mt-3 text-center text-xs text-muted-foreground'>
          Sources: Semrush GEO study (2025), Princeton GEO paper (KDD 2024)
        </p>
      </div>
    </div>
  )
}

/**
 * The 50% Overlap Problem
 */
export function OverlapProblemDiagram() {
  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Zap className='h-5 w-5 text-[#F90B8A]' />
          <span className='text-lg font-semibold text-foreground'>
            The 50% Overlap Problem
          </span>
        </div>
        <div className='flex flex-col items-center gap-6'>
          {/* Visual representation */}
          <div
            className='relative flex items-center justify-center'
            style={{ height: '180px', width: '100%' }}
          >
            {/* SEO Circle */}
            <div
              className='absolute flex items-center justify-center rounded-full border-3'
              style={{
                width: '160px',
                height: '160px',
                left: 'calc(50% - 120px)',
                borderColor: '#6b7280',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
              }}
            >
              <span className='absolute -top-6 text-xs font-bold text-muted-foreground'>
                Google #1
              </span>
            </div>
            {/* GEO Circle */}
            <div
              className='absolute flex items-center justify-center rounded-full border-3'
              style={{
                width: '160px',
                height: '160px',
                left: 'calc(50% - 40px)',
                borderColor: '#008dcc',
                backgroundColor: 'rgba(0, 141, 204, 0.08)',
              }}
            >
              <span className='absolute -top-6 text-xs font-bold text-[#008dcc]'>
                AI Cited
              </span>
            </div>
            {/* Overlap label */}
            <div className='absolute z-10 rounded-lg bg-background px-3 py-1 border border-border/60'>
              <span className='text-lg font-black text-[#F90B8A]'>~50%</span>
              <p className='text-center text-[10px] text-muted-foreground'>
                overlap
              </p>
            </div>
          </div>
          {/* Implications */}
          <div className='grid w-full grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='rounded-lg bg-muted p-3'>
              <div className='mb-1 flex items-center gap-1.5'>
                <XCircle className='h-3.5 w-3.5 text-red-500' />
                <span className='text-xs font-bold text-foreground/80'>
                  What this means
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>
                Half the pages ranking #1 on Google are invisible to AI search.
                Your SEO wins do not automatically transfer.
              </p>
            </div>
            <div className='rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20 p-3'>
              <div className='mb-1 flex items-center gap-1.5'>
                <CheckCircle2 className='h-3.5 w-3.5 text-[#008dcc]' />
                <span className='text-xs font-bold text-foreground/80'>
                  What to do about it
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>
                You need a separate GEO strategy. Audit your AI visibility
                independently from your Google rankings.
              </p>
            </div>
          </div>
        </div>
        <p className='mt-4 text-center text-xs text-muted-foreground'>
          Source: Semrush AI Search Visibility Study, 2025
        </p>
      </div>
    </div>
  )
}

/**
 * Strategy Decision Framework
 */
export function StrategyDecisionDiagram() {
  const scenarios = [
    {
      profile: 'B2B SaaS / Services',
      priority: 'GEO first, SEO second',
      reason:
        'AI referrals convert 5x better. Your buyers are already asking ChatGPT for recommendations.',
      actions: [
        'Build brand authority content',
        'Get cited in industry discussions',
        'Optimize for AI-friendly structure',
      ],
      color: '#008dcc',
    },
    {
      profile: 'E-commerce / DTC',
      priority: 'SEO first, GEO growing',
      reason:
        'Google still drives most purchase-intent traffic. But AI product recommendations are growing 805% YoY.',
      actions: [
        'Maintain technical SEO foundation',
        'Add structured product data',
        'Build product comparison content',
      ],
      color: '#FDC304',
    },
    {
      profile: 'Publishers / Media',
      priority: 'GEO urgently',
      reason:
        'Traffic already dropped 33%. AI Overviews are replacing your content. Adapt or lose more.',
      actions: [
        'Create original research and data',
        'Build journalist brand authority',
        'Diversify revenue beyond traffic',
      ],
      color: '#F90B8A',
    },
    {
      profile: 'Local Business',
      priority: 'SEO + AEO',
      reason:
        'Google Maps and voice search still dominate local discovery. GEO matters less for now.',
      actions: [
        'Optimize Google Business Profile',
        'Target featured snippets',
        'Build review authority',
      ],
      color: '#8b5cf6',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Target className='h-5 w-5 text-[#FDC304]' />
          <span className='text-lg font-semibold text-foreground'>
            Which Strategy Comes First? Decision Framework
          </span>
        </div>
        <div className='space-y-4'>
          {scenarios.map(s => (
            <div
              key={s.profile}
              className='rounded-lg border p-4'
              style={{ borderColor: `${s.color}30` }}
            >
              <div className='mb-2 flex items-center justify-between'>
                <span className='text-sm font-bold text-foreground'>
                  {s.profile}
                </span>
                <span
                  className='rounded-full px-2 py-0.5 text-xs font-bold text-white'
                  style={{ backgroundColor: s.color }}
                >
                  {s.priority}
                </span>
              </div>
              <p className='mb-2 text-xs text-muted-foreground'>{s.reason}</p>
              <div className='flex flex-wrap gap-1.5'>
                {s.actions.map(action => (
                  <span
                    key={action}
                    className='rounded-full px-2 py-0.5 text-[10px] font-medium'
                    style={{
                      backgroundColor: `${s.color}10`,
                      color: s.color,
                    }}
                  >
                    {action}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * AI Platform Citation Comparison
 */
export function PlatformCitationDiagram() {
  const platforms = [
    {
      name: 'ChatGPT',
      citesSource: true,
      realTime: 'Optional (built-in web search)',
      trainingCutoff: 'Varies by model',
      citationStyle: 'Inline links when browsing, brand mentions from training',
      bestFor: 'Brand authority content, training data presence',
      color: '#10b981',
    },
    {
      name: 'Perplexity',
      citesSource: true,
      realTime: 'Always',
      trainingCutoff: 'Real-time',
      citationStyle: 'Numbered footnotes with source links',
      bestFor: 'Fresh, well-structured content with clear data',
      color: '#FDC304',
    },
    {
      name: 'Google AI Overviews',
      citesSource: true,
      realTime: 'Always',
      trainingCutoff: 'Real-time (Google index)',
      citationStyle: 'Source cards with site links',
      bestFor: 'Pages already indexed by Google with strong E-E-A-T',
      color: '#008dcc',
    },
    {
      name: 'Claude',
      citesSource: true,
      realTime: 'Optional (web search, added 2025)',
      trainingCutoff: 'Varies by model',
      citationStyle: 'Cited sources when searching, brand mentions otherwise',
      bestFor: 'Wide web references plus crawlable, structured content',
      color: '#F90B8A',
    },
    {
      name: 'Gemini',
      citesSource: true,
      realTime: 'Optional',
      trainingCutoff: 'Varies',
      citationStyle: 'Inline references with Google Search integration',
      bestFor: 'Google ecosystem content, YouTube presence',
      color: '#8b5cf6',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <BookOpen className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            How Each AI Platform Cites Content
          </span>
        </div>
        <div className='space-y-3'>
          {platforms.map(p => (
            <div
              key={p.name}
              className='rounded-lg border p-3'
              style={{ borderColor: `${p.color}30` }}
            >
              <div className='mb-2 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <div
                    className='h-3 w-3 rounded-full'
                    style={{ backgroundColor: p.color }}
                  />
                  <span className='text-sm font-bold text-foreground'>
                    {p.name}
                  </span>
                </div>
                <div className='flex items-center gap-1'>
                  {p.citesSource ? (
                    <CheckCircle2 className='h-3.5 w-3.5 text-green-500' />
                  ) : (
                    <XCircle className='h-3.5 w-3.5 text-red-400' />
                  )}
                  <span className='text-[10px] text-muted-foreground'>
                    {p.citesSource ? 'Links to sources' : 'No source links'}
                  </span>
                </div>
              </div>
              <div className='grid grid-cols-1 gap-1 text-xs sm:grid-cols-2'>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Data:{' '}
                  </span>
                  <span className='text-foreground/80'>{p.realTime}</span>
                </div>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Best for:{' '}
                  </span>
                  <span className='text-foreground/80'>{p.bestFor}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='mt-4 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20 p-3'>
          <p className='text-xs text-muted-foreground'>
            <strong>Bottom line:</strong> There is no single optimization
            strategy. Each platform has different citation mechanics. GEO means
            optimizing for all of them.
          </p>
        </div>
      </div>
    </div>
  )
}
